import React, { useState, useMemo, useRef, useEffect } from 'react';
import zipcelx from 'zipcelx';
import { cloneDeep } from 'lodash';
import Highcharts, { Options as HighchartsOptions } from 'highcharts';
import '~/shared/utils/highchartsAllowList';
import HCAccessibility from 'highcharts/modules/accessibility';
import HighchartsReact from 'highcharts-react-official';
import HCExporting from 'highcharts/modules/exporting';
import HCOfflineExporting from 'highcharts/modules/offline-exporting';
import HCExportData from 'highcharts/modules/export-data';
import HCNoData from 'highcharts/modules/no-data-to-display';
import styled from 'styled-components';
import reactGA from 'react-ga';
import CustomEvents from 'highcharts-custom-events';
import moment from 'moment';
import { highchartsTooltipCfg } from '~/shared/config/highchartsCfg';
import { hex2rgba } from '../../../utils/colors/hex2rgba';
import { formatTooltipHtml } from '~/shared/utils';
import { buildChartExportFileName } from '~/shared/utils/buildChartExportFileName';

HCExporting(Highcharts);
HCExportData(Highcharts);
HCNoData(Highcharts);
HCOfflineExporting(Highcharts);
CustomEvents(Highcharts as any);
HCAccessibility(Highcharts);

(function (H) {
  H.Chart.prototype.downloadXLS = function () {
    const div = document.createElement('div');
    let xlsxRows = [];
    div.style.display = 'none';
    document.body.appendChild(div);
    const rows = this.getDataRows(true);
    xlsxRows = H.map(rows.slice(1), function (row: any) {
      return H.map(row, function (column: any) {
        let tempColumn = column;
        if (typeof column === 'string' && column?.includes('00:00:00')) {
          tempColumn = moment(column).format('MM/DD/YYYY');
        }
        return {
          type: typeof column === 'number' ? 'number' : 'string',
          value: tempColumn,
        };
      });
    });

    const name = this.options.exporting!.filename;
    zipcelx({
      filename: name as unknown as string,
      sheet: {
        data: xlsxRows,
      },
    });
  };
})(Highcharts);

const EMPTY_OPTIONS: HighchartsOptions = { series: [] };

export interface IStackBarChart {
  axisXLabels: string[];
  axisYLabels: string[];
  values: { XLabel: string; YLabel: string; Value: number }[];
  matrix: number[][];
  data?: any;
}

interface ISeriesObj {
  name: string;
  data: string[];
  color: string | undefined;
}

const Root = styled.div<{ $gridheight: number }>`
  height: ${({ $gridheight }) => ($gridheight ? $gridheight * 118 : 472)}px;
  width: 100%;
  position: relative;
  margin-top: 10px;
`;

const HighchartContainer = styled.div`
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  position: absolute;
  border: solid 1px var(--border-chart);
  background: var(--background-chart);
  padding: 10px;
  .highcharts-legend-item > .highcharts-point {
    transform: unset !important;
  }
  .highcharts-axis-line {
    display: none;
  }
  .highcharts-button-box {
    x: 2;
    y: 1;
    rx: 16;
    ry: 16;
    &:hover {
      fill: #d4e5ff !important;
    }
  }
  .highcharts-button-symbol {
    width: 3.5px !important;
    fill: #4c5e72 !important;
    stroke: transparent !important;
    pointer-events: none;
  }
`;

Highcharts.SVGRenderer.prototype.symbols.ellipsisvertical = function () {
  const path = [
    'M18.1538 16C18.1538 17.1451 17.222 18.0769 16.0769 18.0769C14.9318 18.0769 14 17.1451 14 16C14 14.8549 14.9318 13.9231 16.0769 13.9231C17.222 13.9231 18.1538 14.8549 18.1538 16Z M18.1538 22.9231C18.1538 24.0682 17.222 25 16.0769 25C14.9318 25 14 24.0682 14 22.9231C14 21.778 14.9318 20.8462 16.0769 20.8462C17.222 20.8462 18.1538 21.778 18.1538 22.9231Z M18.1538 9.07692C18.1538 10.222 17.222 11.1538 16.0769 11.1538C14.9318 11.1538 14 10.222 14 9.07692C14 7.93185 14.9318 7 16.0769 7C17.222 7 18.1538 7.93185 18.1538 9.07692Z',
  ];
  return path;
};

export default function TornadoChart({
  data,
  size,
  onClick,
  prism,
  filterValues,
  colorScheme,
  chartTitle,
  chartSubtitle,
  chartYtitle,
  tooltipCfg,
  gridHeight,
  gridIndex,
  gridPrism,
}: any) {
  const chartRef = useRef<any>(null);

  const textBoldShadow = '0 0 .01px black, 0 0 .01px black, 0 0 .01px black';

  const [selectedLabel, setSelectedLabel] = useState(false);
  const [labelClicked, setLabelClicked] = useState(false);

  const exportFileName = buildChartExportFileName(chartTitle, filterValues, prism);

  useEffect(() => {
    const onScroll = () => {
      const elements = document.getElementsByClassName('xLabel');

      for (let i = 0; i < elements.length; i++) {
        (elements[i] as unknown as any).style.display = 'none';
      }
    };
    // clean up code
    window.removeEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    function handleClick(e: any) {
      const file = (e.target as Element).getAttribute('download');
      if (file) {
        const fileType = file.substr(file.length - 3, 3);
        reactGA.event({ category: 'User', action: `Downloaded ${fileType}: ${file}` });
      }
    }
    window.addEventListener('click', handleClick);

    return function cleanupListener() {
      window.removeEventListener('click', handleClick);
    };
  }, [chartRef]);

  useEffect(() => {
    chartRef.current.reflow();
  }, [chartRef, size]);

  const chartOptions = useMemo(() => {
    const sortedData: IStackBarChart = cloneDeep({
      axisYLabels: data?.row_nodes?.map((r: any) => r.name),
      axisXLabels: data?.col_nodes?.map((c: any) => c.name),
      matrix: data?.data,
      data: data,
      values: [],
    });

    const categoriesData: string[] = [];
    const seriesData: any[] = [];

    for (let j = 0; j < sortedData.axisXLabels?.length; ++j) {
      const seriesObj: ISeriesObj = {
        name: sortedData.axisXLabels[sortedData.axisXLabels?.length - j - 1],
        data: [],
        color: colorScheme ? colorScheme[sortedData.axisXLabels?.length - j - 1] : undefined,
      };
      for (let i = 0; i < sortedData.axisYLabels?.length; ++i) {
        if (j === 0) categoriesData.push(sortedData.axisYLabels[i]);
        const objData = data.data[i][sortedData.axisXLabels?.length - j - 1] as unknown as string;
        seriesObj.data.push(objData);
      }
      seriesData.push(seriesObj);
    }

    if (!data) return;
    return {
      chart: {
        className: prism.replace(' ', '') + gridIndex,
        width: size.width - 20,
        spacingRight: 20,
        spacingTop: chartSubtitle ? 36 : 24,
        marginLeft: 190,
        height: (gridHeight / 4) * 450 + (gridHeight / 4 - 1) * 38,
        type: 'bar',
        style: {
          fontFamily: ['Montserrat', 'sans-serif'],
        },
      },
      exporting: {
        filename: exportFileName,
        csv: {
          columnHeaderFormatter: function (item: any) {
            if (item && item instanceof Highcharts.Axis) {
              if ((item as unknown as any).type === 'datetime') {
                return 'Date';
              }
              return 'Category';
            }
            return {
              columnTitle: item.name,
            };
          },
          dateFormat: '%m/%d/%Y',
        },
        enabled: true,
        buttons: {
          contextButton: {
            x: 0,
            y: -18,
            symbol: 'ellipsisvertical',
            menuClassName: 'highcharts-indicator-menu',
            menuItems: [
              'viewFullscreen',
              'downloadPNG',
              'downloadSVG',
              'downloadPDF',
              'separator',
              'downloadXLS',
              'downloadCSV',
            ],
          },
        },
        fallbackToExportServer: false,
      },
      navigation: {
        menuItemHoverStyle: {
          background: '#EAF2FF',
        },
        menuItemStyle: {
          height: 30,
          fill: '#33485E',
          fontSize: 15,
          padding: '6px 12px',
        },
      },
      title: {
        text: chartTitle,
        align: 'left',
        x: 21,
        y: 0,
        style: {
          fontSize: '18px',
          color: 'var(--color-chart-title)',
          fontWeight: 500,
          letterSpacing: '0.5px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      },
      subtitle: {
        align: 'left',
        text: chartSubtitle,
        x: 22,
        y: 18,
        style: {
          color: 'var(--color-chart-title)',
          fontWeight: 'normal',
          letterSpacing: '0.5px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        categories: categoriesData,
        gridZIndex: 0,
        zIndex: 0,
        labels: {
          formatter: function (): string {
            const obj = this as unknown as any;
            return `<div data-id='${`clickableLabel${prism.replace(' ', '')}${gridIndex}`}' id='${
              data.row_nodes[obj.pos].id
            }#${data.title}' data-name='${obj.value}' style='font-size:15px; text-shadow: ${
              data?.row_nodes?.filter((c: any) => String(selectedLabel).includes(c.id))[0]?.name === obj.value &&
              prism === gridPrism
                ? textBoldShadow
                : 'none'
            }' class="clickableLabel">${obj.value.length > 16 ? `${obj.value.substring(0, 13)}...` : obj.value}</div>`;
          },
          events: {
            click: function (e: any) {
              const cellInfo = {
                source: 'value',
                topic: e.target.id.split('#')[0],
              };
              const obj = this as unknown as any;
              const name = obj.value.split("'")[5];
              const value = obj.value.split("'")[3];
              const objOrg = data.row_nodes.filter((l: any) => l.name === value);
              const description = objOrg[0]?.description;
              const chart = chartRef.current;
              if (chart.xAxisTooltip && (name.length > 16 || description)) {
                chart.xAxisTooltip.destroy();
                chart.xAxisTooltip = undefined;
              }

              const elements = document.getElementsByClassName('clickableLabel');

              for (let i = 0; i < elements.length; i++) {
                (elements[i] as unknown as any).style.textShadow = 'none';
              }

              setSelectedLabel(e.target.id);
              setLabelClicked(true);
              onClick(cellInfo);
            },
            mouseover: function () {
              if (labelClicked) return;
              const chartBox = document.querySelector(`.${prism.replace(' ', '')}${gridIndex}`);
              const chartRect = chartBox && chartBox.getBoundingClientRect();
              const obj = this as unknown as any;
              const value = obj.value.split("'")[3];
              const name = obj.value.split("'")[5];
              const objBox = document.querySelector(`[id="${value}"`);
              const objRect = objBox && objBox.getBoundingClientRect();
              const objOrg = data.row_nodes.filter((l: any) => l.name === value);
              const description = objOrg[0]?.description;
              if (name.length < 17 && !description) return;
              const chart = chartRef.current;
              if (chart.xAxisTooltip !== undefined) chart.xAxisTooltip = undefined;
              const tooltipHtml = formatTooltipHtml({
                title: name,
                description,
                tooltipCfg,
                letterSpacingAdjustment: -0.75,
              });
              chart.xAxisTooltip = chart.renderer
                .label(tooltipHtml, 0, 0, 'rectangle', undefined, undefined, true)
                .css({
                  color: tooltipCfg.color,
                  width: tooltipCfg.maxWidth,
                  opacity: 0,
                })
                .attr({
                  fill: hex2rgba(tooltipCfg.backgroundColor, tooltipCfg.opacity),
                  filter: 'drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.35))',
                  'dominant-baseline': 'auto',
                  'stroke-width': tooltipCfg.borderWidth,
                  'stroke-dasharray':
                    tooltipCfg.borderStyle === 'solid' ? 'none' : tooltipCfg.borderStyle === 'dotted' ? '2,2' : '5,5',
                  'stroke-opacity': tooltipCfg.opacity,
                  padding: Number(tooltipCfg.padding.replace('px', '')),
                  r: Number(tooltipCfg.borderRadius.replace('px', '')),
                  class: 'xLabel',
                })
                .add()
                .toFront();
              chart.xAxisTooltipArrow = chart.renderer
                .label(``, 0, 0, 'rectangle')
                .css({
                  color: tooltipCfg.color,
                  width: tooltipCfg.maxWidth,
                  opacity: 0,
                })
                .attr({
                  fill: hex2rgba(tooltipCfg.backgroundColor, tooltipCfg.opacity),
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))',
                  height: 4,
                  width: 4,
                  'dominant-baseline': 'auto',
                  'stroke-width': tooltipCfg.borderWidth,
                  'stroke-dasharray':
                    tooltipCfg.borderStyle === 'solid' ? 'none' : tooltipCfg.borderStyle === 'dotted' ? '2,2' : '5,5',
                  'stroke-opacity': tooltipCfg.opacity,
                  padding: 2,
                  r: 0,
                  class: 'xLabelArrow',
                })
                .add()
                .toFront();
              chart.xAxisTooltipArrowBlocker = chart.renderer
                .label(``, 0, 0, 'rectangle')
                .css({
                  color: tooltipCfg.color,
                  width: tooltipCfg.maxWidth,
                  opacity: 0,
                })
                .attr({
                  fill: hex2rgba(tooltipCfg.backgroundColor, tooltipCfg.opacity),
                  height: 3,
                  width: 17,
                  'dominant-baseline': 'auto',
                  'stroke-width': tooltipCfg.borderWidth,
                  'stroke-dasharray':
                    tooltipCfg.borderStyle === 'solid' ? 'none' : tooltipCfg.borderStyle === 'dotted' ? '2,2' : '5,5',
                  'stroke-opacity': tooltipCfg.opacity,
                  padding: 2,
                  r: 0,
                  class: 'xLabelArrowBlocker',
                })
                .add()
                .toFront();
              const tooltipRect = chart.xAxisTooltip.getBBox();
              const tooltipBorderWidth = Number(tooltipCfg.borderWidth.replace('px', ''));
              let tooltipX =
                objRect && objRect !== null && chartRect && chartRect !== null
                  ? objRect!.x + objRect!.width / 2 - chartRect!.x - tooltipRect.width / 2
                  : 0;
              if (tooltipX < tooltipBorderWidth) tooltipX = tooltipBorderWidth;
              if (tooltipX + tooltipRect.width > chartRect!.width)
                tooltipX = chartRect!.width - tooltipRect.width - tooltipBorderWidth;
              chart.xAxisTooltip
                .attr({
                  x: 20,
                  y:
                    objRect && objRect !== null && chartRect && chartRect !== null
                      ? objRect!.y - chartRect!.y - tooltipRect.height - 10
                      : 0,
                })
                .css({
                  opacity: 1,
                });
              chart.xAxisTooltipArrow
                .attr({
                  rotation: 45,
                  x: 20 + tooltipRect.width / 2 + 4,
                  y: objRect ? objRect!.y - chartRect!.y - tooltipRect.height + 15 : 0,
                })
                .css({
                  opacity: 1,
                });
              chart.xAxisTooltipArrowBlocker
                .attr({
                  x: 20 + tooltipRect.width / 2 - 7,
                  y: objRect ? objRect!.y - chartRect!.y - tooltipRect.height + 14 : 0,
                })
                .css({
                  opacity: 1,
                });
              const htmlObj = document.querySelector<HTMLElement>(`[data-id='${value}']`);
              if (htmlObj) htmlObj.style.textShadow = textBoldShadow;
            },
            mouseout: function () {
              const obj = this as unknown as any;
              const value = obj.value.split("'")[3];
              const name = obj.value.split("'")[5];
              const objOrg = data.row_nodes.filter((l: any) => l.name === value);
              const description = objOrg[0]?.description;
              if (name.length < 17 && !description) return;
              if (labelClicked) return setLabelClicked(false);
              const chart = chartRef.current;
              if (chart?.xAxisTooltip) {
                chart.xAxisTooltip.destroy();
              }
              if (chart?.xAxisTooltipArrow) {
                chart.xAxisTooltipArrow.destroy();
              }
              if (chart?.xAxisTooltipArrowBlocker) {
                chart.xAxisTooltipArrowBlocker.destroy();
              }
              if (value === selectedLabel) return;
              const htmlObj = document.querySelector<HTMLElement>(`[data-id='${value}']`);
              if (htmlObj) htmlObj.style.textShadow = 'none';
            },
          },
          useHTML: false,
          style: {
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            fontSize: '14px',
            zIndex: 0,
          },
          gridZIndex: 0,
          zIndex: 0,
          x: -160,
          align: 'left',
          minWidth: 160,
          width: 160,
        },
      },
      yAxis: {
        endOnTick: false,
        gridZIndex: -1,
        zIndex: -1,
        title: {
          text: chartYtitle,
          enabled: true,
        },
        stackLabels: {
          enabled: false,
        },
        labels: {
          useHTML: true,
          style: {
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            fontSize: '14px',
          },
        },
      },
      legend: { enabled: false },
      tooltip: {
        ...highchartsTooltipCfg(tooltipCfg),
        useHTML: true,
        backgroundColor: '#fff',
        borderRadius: 5,
        borderColor: 'transparent',
        shadow: true,
      },
      plotOptions: {
        series: {
          borderRadius: {
            radius: 10,
          },
          stacking: 'normal',
          maxPointWidth: 30,
          cursor: 'pointer',
          point: {
            events: {
              click: (e: any) => {
                const cellInfo = {
                  source: 'value',
                  topic: data.row_nodes[e.point.index].id,
                };
                setSelectedLabel(data.row_nodes[e.point.index].id);
                setLabelClicked(true);
                onClick(cellInfo);
              },
            },
          },
        },
        bar: {
          borderWidth: 0,
          stacking: 'normal',
          dataLabels: {
            enabled: false,
          },
        },
      },
      series: seriesData,
    };
  }, [
    data,
    size,
    exportFileName,
    onClick,
    chartRef,
    labelClicked,
    selectedLabel,
    colorScheme,
    tooltipCfg,
    chartTitle,
    chartSubtitle,
    chartYtitle,
    gridIndex,
    prism,
    gridPrism,
  ]);

  return (
    <Root $gridheight={gridHeight}>
      <HighchartContainer>
        <HighchartsReact
          containerProps={{ style: { height: '100%' } }}
          style={{ flex: 1 }}
          highcharts={Highcharts}
          constructorType={'chart'}
          options={chartOptions ?? EMPTY_OPTIONS}
          callback={(chart: any) => (chartRef.current = chart)}
        />
      </HighchartContainer>
    </Root>
  );
}
